/**
 * Round-trip YAML editing.
 *
 * Content files are written by people and by other tools (Sveltia, Claude Code, Stripe webhooks), so a save must
 * change only what the editor changed: comments, quoting, flow vs. block style, key order and blank lines of every
 * untouched value stay byte for byte. `updateYaml` does that by patching the source text at the positions the
 * parser reports, and only regenerates the smallest node that actually changed.
 *
 * Every patch is verified by parsing the result again; if that ever fails to produce the wanted data, the whole
 * document is regenerated with the `yaml` library instead (correct, just less tidy).
 */
import {
  Document,
  isMap,
  isPair,
  isScalar,
  isSeq,
  parseDocument,
  Scalar,
  type Node as YamlNode,
  type Pair,
  type ToStringOptions,
  YAMLMap,
  YAMLSeq,
} from 'yaml';

export type YamlValue = null | boolean | number | string | YamlValue[] | { [key: string]: YamlValue };

/** Parse YAML into plain data. Timestamps stay strings (YAML 1.2 core schema), so dates round-trip exactly. */
export function parseYaml(text: string): YamlValue {
  const doc = parseDocument(text);
  if (doc.errors.length) throw new YamlError(doc.errors[0]!.message);
  return (doc.toJS() ?? null) as YamlValue;
}

export class YamlError extends Error {}

interface Edit {
  start: number;
  end: number;
  text: string;
}

interface Style {
  indentSeq: boolean;
  flowPadding: boolean;
}

export interface UpdateResult {
  text: string;
  /** true when the precise patcher could not be used and the document was regenerated. */
  regenerated: boolean;
}

/** Return `source` changed so that it parses to `next`, touching as little text as possible. */
export function updateYaml(source: string, next: YamlValue): string {
  return updateYamlDetailed(source, next).text;
}

export function updateYamlDetailed(source: string, next: YamlValue): UpdateResult {
  const doc = parseDocument(source);
  if (doc.errors.length) throw new YamlError(doc.errors[0]!.message);
  const prev = (doc.toJS() ?? null) as YamlValue;
  if (deepEqual(prev, next)) return { text: source, regenerated: false };

  const style = detectStyle(source);
  if (doc.contents && source.trim() !== '') {
    try {
      const edits: Edit[] = [];
      const root = doc.contents as YamlNode;
      const ok = diffNode(source, root, prev, next, edits, style, 0);
      if (ok) {
        const text = applyEdits(source, edits);
        const check = parseDocument(text);
        if (!check.errors.length && deepEqual(check.toJS() ?? null, next)) return { text, regenerated: false };
      }
    } catch {
      // fall through to regeneration
    }
  }
  return { text: regenerate(doc, next, style), regenerated: true };
}

/** Serialise data as new YAML (used for new files). */
export function stringifyYaml(value: YamlValue, style: Partial<Style> = {}): string {
  const doc = new Document(value);
  return doc.toString(toStringOptions({ indentSeq: true, flowPadding: false, ...style }));
}

function toStringOptions(style: Style): ToStringOptions {
  return {
    lineWidth: 0,
    minContentWidth: 0,
    indentSeq: style.indentSeq,
    flowCollectionPadding: style.flowPadding,
  };
}

function regenerate(doc: Document, next: YamlValue, style: Style): string {
  // Keep the comment at the top of the file; everything else is rebuilt.
  const fresh = new Document(next);
  fresh.commentBefore = doc.commentBefore;
  fresh.comment = doc.comment;
  return fresh.toString(toStringOptions(style));
}

function detectStyle(source: string): Style {
  // Block sequences written at the same column as their key ("key:\n- item") mean indentSeq is off.
  const unindented = /^( *)[^\s#-][^\n]*:[ \t]*\n\1- /m.test(source);
  const indented = /^( *)[^\s#-][^\n]*:[ \t]*\n\1 +- /m.test(source);
  const padded = /[[{] \S/.test(source);
  const tight = /[[{][^\s\]}]/.test(source);
  return { indentSeq: indented || !unindented, flowPadding: padded && !tight };
}

/**
 * Compare a node with its new value and push the edits that turn one into the other.
 * Returns false when the node has to be regenerated by its parent.
 */
function diffNode(
  src: string,
  node: YamlNode,
  prev: YamlValue,
  next: YamlValue,
  edits: Edit[],
  style: Style,
  depth: number,
): boolean {
  if (deepEqual(prev, next)) return true;
  if (isMap(node) && isPlainObject(prev) && isPlainObject(next)) {
    return diffMap(src, node, prev, next, edits, style, depth);
  }
  if (isSeq(node) && Array.isArray(prev) && Array.isArray(next) && prev.length === next.length) {
    return diffSeq(src, node, prev, next, edits, style, depth);
  }
  if (isScalar(node) && !isCollection(next)) {
    return replaceScalar(src, node, next, edits, style);
  }
  return false;
}

function diffMap(
  src: string,
  map: YAMLMap,
  prev: Record<string, YamlValue>,
  next: Record<string, YamlValue>,
  edits: Edit[],
  style: Style,
  depth: number,
): boolean {
  const pairs = map.items as Pair<YamlNode, YamlNode | null>[];
  const keyOf = (p: Pair<YamlNode, YamlNode | null>) => String(isScalar(p.key) ? p.key.value : p.key);
  const nextKeys = Object.keys(next);
  const prevKeys = Object.keys(prev);
  // Duplicate or complex keys: let the parent regenerate.
  if (pairs.length !== prevKeys.length) return false;

  for (const pair of pairs) {
    const key = keyOf(pair);
    if (!(key in next)) {
      if (map.flow || pairs.length === 1) return false;
      edits.push(removePair(src, pair));
      continue;
    }
    if (deepEqual(prev[key], next[key])) continue;
    const value = pair.value;
    const handled =
      value && value.range && isNodeOk(value) && diffNode(src, value, prev[key]!, next[key]!, edits, style, depth + 1);
    if (!handled) {
      if (!replacePair(src, map, pair, key, next[key]!, edits, style)) return false;
    }
  }

  const added = nextKeys.filter((k) => !(k in prev));
  if (added.length) {
    if (map.flow || !pairs.length) return false;
    for (const key of added) {
      // Insert before the first existing key that follows it in the new order, else after the last pair.
      const after = nextKeys.slice(nextKeys.indexOf(key) + 1).find((k) => k in prev);
      const before = after ? pairs.find((p) => keyOf(p) === after) : undefined;
      const indent = columnOf(src, (pairs[0]!.key as YamlNode).range![0]);
      const text = renderPair(key, next[key]!, indent, style, null);
      if (before) {
        const at = lineStart(src, leadingCommentStart(src, (before.key as YamlNode).range![0]));
        edits.push({ start: at, end: at, text: ' '.repeat(indent) + text + '\n' });
      } else {
        const last = pairs[pairs.length - 1]!;
        const at = endOfPairLine(src, last);
        edits.push({ start: at, end: at, text: ' '.repeat(indent) + text + '\n' });
      }
    }
  }
  // Reordered keys are a real change too, but content editors never reorder object keys; ignore order.
  return true;
}

function diffSeq(
  src: string,
  seq: YAMLSeq,
  prev: YamlValue[],
  next: YamlValue[],
  edits: Edit[],
  style: Style,
  depth: number,
): boolean {
  const items = seq.items as YamlNode[];
  if (items.length !== prev.length) return false;
  for (let i = 0; i < items.length; i++) {
    if (deepEqual(prev[i], next[i])) continue;
    const item = items[i]!;
    if (!item || !item.range || isPair(item)) return false;
    if (!diffNode(src, item, prev[i]!, next[i]!, edits, style, depth + 1)) return false;
  }
  return true;
}

function isNodeOk(node: YamlNode): boolean {
  // Aliases and anchored nodes are shared; editing them in place would change other places too.
  return !('anchor' in node && (node as { anchor?: string }).anchor) && node.constructor.name !== 'Alias';
}

function isCollection(v: YamlValue): boolean {
  return Array.isArray(v) || isPlainObject(v);
}

function isPlainObject(v: unknown): v is Record<string, YamlValue> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function replaceScalar(src: string, node: Scalar, next: YamlValue, edits: Edit[], style: Style): boolean {
  if (!node.range) return false;
  const [start, valueEnd] = node.range;
  const original = src.slice(start, valueEnd);
  const inFlow = isInFlow(src, start);
  let rendered = renderScalar(next, node, inFlow, style);
  if (rendered.includes('\n')) {
    // Block scalar: its content must be indented more than the line that holds the key / dash.
    // A block scalar rendered on its own has its content at column 0; indent it under its key or dash.
    const indent = indentOfLine(src, start) + 2;
    const lines = rendered.split('\n');
    rendered = [lines[0], ...lines.slice(1).map((l) => (l ? ' '.repeat(indent) + l : l))].join('\n');
  }
  // A block scalar's range runs up to and including its final line break; keep the text around it intact.
  const trailing = original.match(/\n*$/)![0];
  rendered = rendered.replace(/\n*$/, '') + trailing;
  edits.push({ start, end: valueEnd, text: rendered });
  return true;
}

function renderScalar(value: YamlValue, old: Scalar | null, inFlow: boolean, style: Style): string {
  const node = new Scalar(value);
  if (old && typeof value === 'string' && typeof old.value === 'string') {
    const multiline = value.includes('\n');
    if (!multiline || old.type === Scalar.BLOCK_LITERAL || old.type === Scalar.BLOCK_FOLDED) {
      node.type = old.type === Scalar.BLOCK_FOLDED ? Scalar.BLOCK_LITERAL : old.type;
    }
  }
  if (inFlow && typeof value === 'string' && value.includes('\n')) node.type = Scalar.QUOTE_DOUBLE;
  const doc = new Document(node);
  let out = doc.toString(toStringOptions(style));
  if (out.endsWith('\n')) out = out.slice(0, -1);
  return out;
}

/** Replace a whole `key: value` pair with freshly rendered YAML in the file's style. */
function replacePair(
  src: string,
  map: YAMLMap,
  pair: Pair<YamlNode, YamlNode | null>,
  key: string,
  value: YamlValue,
  edits: Edit[],
  style: Style,
): boolean {
  const keyNode = pair.key as YamlNode;
  if (!keyNode?.range) return false;
  if (map.flow) {
    // Inside `{ a: 1 }`: replace just the value, rendered as flow.
    if (!pair.value?.range) return false;
    const node = new Document(value).createNode(value, { flow: true });
    const text = new Document(node).toString(toStringOptions(style)).replace(/\n$/, '');
    edits.push({ start: pair.value.range[0], end: pair.value.range[1], text });
    return true;
  }
  const start = keyNode.range[0];
  const end = pairValueEnd(src, pair);
  const indent = columnOf(src, start);
  const old = pair.value;
  const original = src.slice(start, end);
  const flow = !!(old && (isMap(old) || isSeq(old)) && old.flow);
  let text = renderPair(key, value, indent, style, old ?? null, flow);
  const trailing = original.match(/\n*$/)![0];
  text = text.replace(/\n*$/, '') + trailing;
  edits.push({ start, end, text });
  return true;
}

function renderPair(
  key: string,
  value: YamlValue,
  indent: number,
  style: Style,
  old: YamlNode | null,
  flow = false,
): string {
  const doc = new Document({});
  let valueNode: YamlNode;
  if (isCollection(value)) {
    valueNode = doc.createNode(value, { flow }) as YamlNode;
  } else {
    valueNode = new Scalar(value);
    if (old && isScalar(old) && typeof value === 'string' && typeof old.value === 'string' && !value.includes('\n')) {
      (valueNode as Scalar).type = old.type;
    }
  }
  const map = new YAMLMap();
  map.items.push(doc.createPair(key, valueNode) as Pair<unknown, unknown>);
  doc.contents = map;
  const out = doc.toString(toStringOptions(style)).replace(/\n$/, '');
  return out
    .split('\n')
    .map((line, i) => (i === 0 || !line ? line : ' '.repeat(indent) + line))
    .join('\n');
}

function removePair(src: string, pair: Pair<YamlNode, YamlNode | null>): Edit {
  const keyStart = (pair.key as YamlNode).range![0];
  const start = lineStart(src, keyStart);
  const end = endOfPairLine(src, pair);
  return { start, end, text: '' };
}

function pairValueEnd(src: string, pair: Pair<YamlNode, YamlNode | null>): number {
  const value = pair.value;
  if (value?.range) return value.range[1];
  const keyEnd = (pair.key as YamlNode).range![1];
  // `key:` with no value: end after the colon.
  const colon = src.indexOf(':', keyEnd);
  return colon >= 0 ? colon + 1 : keyEnd;
}

/** Offset just after the line break that ends a pair (including a trailing comment on its last line). */
function endOfPairLine(src: string, pair: Pair<YamlNode, YamlNode | null>): number {
  let pos = pairValueEnd(src, pair);
  if (src[pos - 1] === '\n') return pos;
  const nl = src.indexOf('\n', pos);
  return nl < 0 ? src.length : nl + 1;
}

function leadingCommentStart(src: string, offset: number): number {
  // Keep comments that sit on the lines right above a key attached to that key.
  let start = lineStart(src, offset);
  while (start > 0) {
    const prevStart = lineStart(src, start - 1);
    const line = src.slice(prevStart, start - 1);
    if (/^\s*#/.test(line)) start = prevStart;
    else break;
  }
  return start;
}

function lineStart(src: string, offset: number): number {
  return src.lastIndexOf('\n', offset - 1) + 1;
}

function columnOf(src: string, offset: number): number {
  return offset - lineStart(src, offset);
}

function indentOfLine(src: string, offset: number): number {
  const start = lineStart(src, offset);
  let i = start;
  while (src[i] === ' ' || src[i] === '-') {
    if (src[i] === '-' && src[i + 1] !== ' ') break;
    i++;
  }
  // For "  - key: value" the content column is after the dash.
  return i - start;
}

function isInFlow(src: string, offset: number): boolean {
  const line = src.slice(lineStart(src, offset), offset);
  let depth = 0;
  let quote: string | null = null;
  for (const ch of line) {
    if (quote) {
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '[' || ch === '{') depth++;
    else if (ch === ']' || ch === '}') depth--;
  }
  return depth > 0;
}

function applyEdits(src: string, edits: Edit[]): string {
  const sorted = [...edits].sort((a, b) => b.start - a.start || b.end - a.end);
  let out = src;
  let lastStart = Infinity;
  for (const e of sorted) {
    if (e.end > lastStart) throw new Error('overlapping edits');
    out = out.slice(0, e.start) + e.text + out.slice(e.end);
    lastStart = e.start;
  }
  return out;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object') {
    if (Array.isArray(b)) return false;
    const ak = Object.keys(a as object);
    const bk = Object.keys(b as object);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => k in (b as object) && deepEqual((a as never)[k], (b as never)[k]));
  }
  return false;
}
